import { useState } from 'react';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import {
  Alert,
  Anchor,
  Button,
  FileButton,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import {
  EKIPMAN_KONTROL_SONUCU_ETIKETI,
  EKIPMAN_KONTROL_YETKILISI_ETIKETI,
  type EquipmentDurumGecmisi,
  type KapiDurumu,
} from '@isg/validation';
import {
  useEkipmanDurumGecisi,
  useEkipmanKontrolleri,
  useGorevSecenekleri,
  useKontrolKaydiEkle,
  useKontrolKaydiSil,
  useKontrolRaporuYukle,
} from '../api/queries';
import { gunGirdisiIso, tarihGoster } from '../lib/format';
import { EKIPMAN_DURUM_ETIKETI } from '../i18n/tr';
import { Bolum, KapiSebebi, Rozet, SatirEylemleri, SolukSatir } from '../sozlesme';
import { ANLAM } from '../theme';

/**
 * EKİPMAN DETAY SAYFASININ BÖLÜMLERİ (Y1 — tur 4 artçısı).
 *
 * ✏️ **İKİ AYRI SAYFA BURADA BİRLEŞTİ:** `/ekipman/:id/durum` ve
 * `/ekipman/:id/kontroller` **kendi sayfalarıydı** ve ikisi de aynı kaydın
 * bir parçasını gösteriyordu — kullanıcı künyeyi listenin modalinde, durumu
 * bir sayfada, kontrol geçmişini başka bir sayfada arıyordu. **P5 bunun
 * tersini söyler: kayıt detayında SEKME YOKTUR, bölümler ALT ALTA durur** ve
 * *"PDF'e giden şey sayfanın kendisidir"*. Eski adresler ölmedi, yeni sayfaya
 * yönlendirilir (kabuğun kendi kuralı).
 */

/**
 * DURUM + GEÇİŞ GEÇMİŞİ (F4·S5·B2; T10/#5 · #6, Kapsam §16.5/G5-Z7).
 *
 * ⚠️ **PASİFLİK ARŞİV DEĞİLDİR.** Pasif ekipmanın künyesi düzenlenebilir,
 * kontrol geçmişi yazılabilir; **tek sonucu görev ÜRETMEMESİ** ve seçilebilir
 * havuzlardan düşmesidir. Ekranda "salt okunur" dili KULLANILMAZ.
 *
 * ⚠️ **PASİFE ALMANIN SONUCU ÖNCEDEN SÖYLENİR:** açık görevler `gecersiz`
 * kapanır — `kacirildi` DEĞİL (Z5: kimse yapmadı ama **yapması da
 * gerekmiyordu**).
 *
 * ⚠️ **ONAY MODALİ SÖZLEŞMENİN İSTİSNASI DEĞİL, TAM OLARAK KAPSAMIDIR** (P1):
 * modal ana desen olmaktan çıktı ama **onay sorularına** kaldı — bu bir onay
 * sorusudur ve gerekçe ZORUNLUDUR (DB CHECK + şema, çift kilit).
 */
export function DurumBolumu({
  equipmentId,
  veri,
}: {
  equipmentId: string;
  veri: EquipmentDurumGecmisi;
}): React.JSX.Element {
  const gecis = useEkipmanDurumGecisi();
  const [onayAcik, setOnayAcik] = useState(false);
  const [gerekce, setGerekce] = useState('');
  const hedef = veri.durum === 'aktif' ? 'pasif' : 'aktif';

  return (
    <>
      {gecis.isError && (
        <Alert color={ANLAM.engel} role="alert">
          {gecis.error.message}
        </Alert>
      )}
      <Bolum baslik="Durum" testId="ekipman-durum-kunye">
        <Group justify="space-between" align="flex-start">
          <Text size="sm">
            {veri.durum === 'aktif'
              ? 'Aktif ekipman periyodik görev üretir ve iş izninde seçilebilir.'
              : 'Pasif ekipman GÖREV ÜRETMEZ ve seçilebilir havuzlardan düşer; künyesi ve kontrol geçmişi düzenlenebilir kalır. Aktife dönerken KONTROL İSTER.'}
          </Text>
          {/* DÜĞME GÖRÜNÜRLÜĞÜ SUNUCUDAN (`gecisYapabilir`) — ekran ikinci
              şapka listesi yazmaz; görünmeyen düğmenin ucu da 403 verir. */}
          {veri.gecisYapabilir && (
            <Button
              size="xs"
              variant="light"
              onClick={() => setOnayAcik(true)}
              data-testid="ekipman-durum-gecis"
            >
              {hedef === 'pasif' ? 'Pasife al' : 'Aktife al'}
            </Button>
          )}
        </Group>
        {veri.durum === 'aktif' && veri.acikGorevSayisi > 0 && (
          <Text size="sm" c="dimmed" mt="xs" data-testid="ekipman-acik-gorev">
            Pasife alınırsa {veri.acikGorevSayisi} açık görev "geçersiz" olarak kapanır (kaçırılmış
            sayılmaz).
          </Text>
        )}
      </Bolum>

      <Bolum
        baslik="Geçiş geçmişi"
        aciklama="Her geçiş değişmez kayda yazılır: kim, ne zaman, hangi gerekçeyle."
        testId="ekipman-durum-gecmisi"
      >
        {veri.gecisler.length === 0 ? (
          <Text size="sm" c="dimmed">
            Henüz durum geçişi yok.
          </Text>
        ) : (
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Tarih</Table.Th>
                <Table.Th>Geçiş</Table.Th>
                <Table.Th>Gerekçe</Table.Th>
                <Table.Th>Yapan</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {veri.gecisler.map((satir) => (
                <Table.Tr key={satir.id} data-testid="ekipman-gecis-satiri">
                  <Table.Td>{tarihGoster(satir.createdAt)}</Table.Td>
                  {/* ⚠️ **K9/D2 — HAM ENUM EKRANA BASILMAZ.** */}
                  <Table.Td>
                    {EKIPMAN_DURUM_ETIKETI[satir.oncekiDurum]} →{' '}
                    {EKIPMAN_DURUM_ETIKETI[satir.yeniDurum]}
                  </Table.Td>
                  <Table.Td>{satir.gerekce}</Table.Td>
                  <Table.Td>{satir.yapanAd ?? '—'}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Bolum>

      <Modal
        opened={onayAcik}
        onClose={() => setOnayAcik(false)}
        title={hedef === 'pasif' ? 'Ekipmanı pasife al' : 'Ekipmanı aktife al'}
      >
        <Stack>
          {hedef === 'pasif' ? (
            <Text size="sm">
              Açık {veri.acikGorevSayisi} görev "geçersiz" olarak kapanacak — bunlar kaçırılmış
              sayılmaz, çünkü yapılmaları da gerekmiyordu.
            </Text>
          ) : (
            <Text size="sm">
              Ekipman aktife döndüğünde motor yeni bir vade doğurur ve o vade geçmiş sayılır:
              ekipman kontrolsüz kalkmaz.
            </Text>
          )}
          <Textarea
            label="Gerekçe"
            required
            value={gerekce}
            onChange={(olay) => setGerekce(olay.currentTarget.value)}
            data-testid="ekipman-durum-gerekce"
          />
          <Button
            disabled={gerekce.trim().length === 0}
            loading={gecis.isPending}
            onClick={() =>
              gecis.mutate(
                { equipmentId, input: { durum: hedef, gerekce } },
                {
                  onSuccess: () => {
                    setOnayAcik(false);
                    setGerekce('');
                  },
                },
              )
            }
            data-testid="ekipman-durum-onayla"
          >
            Onayla
          </Button>
        </Stack>
      </Modal>
    </>
  );
}

/**
 * KONTROL GEÇMİŞİ (tur 4 / K1 — Kapsam §16.5; sayfa sözleşmesi P1 · P2).
 *
 * ⚠️ **`son_kontrol` BU LİSTEDEN TÜRETİLİR** — künyede düzenlenemez. Bölümün
 * açıklaması bunu **söyler**, çünkü kullanıcı tarihi künyede arayacak ve
 * bulamayınca *"nereye yazacağım"* diye soracaktır (§2.40'ın sunum kolu:
 * pasif/olmayan bir alanın SEBEBİ yanında durur).
 *
 * ⚠️ **UYGUNSUZ KONTROL VADEYİ İLERLETMEZ** ve bu bölümde görünür: satır
 * *"Uygun değil"* rozetiyle durur, künyedeki *"Son kontrol"* değeri değişmez.
 *
 * ⚠️ **KONTROL YETKİLİSİ SINIFI SEÇTİRİLMEZ** — sunucu ekipman tipinden
 * çözer ve satıra yazar; ekran onu **okur** (§19/11-4: kural TİPTEDİR).
 *
 * ✏️ **P13 · B3 (yüzey dürüstlüğü turu · ikinci oturum):** ekleme satırı,
 * rapor yükleme ve silme **herkese** çiziliyordu; yazamayan basınca 403
 * alıyordu. Artık üçü de `ekipmanKapisi`nden (kontrol uçlarının adım listesi:
 * şapka → bahçe → arşiv) — yazamayana tek sebep satırı.
 */
export function KontrolGecmisiBolumu({
  equipmentId,
  ekipmanKapisi,
}: {
  equipmentId: string;
  ekipmanKapisi: KapiDurumu | undefined;
}): React.JSX.Element {
  const yazabilir = ekipmanKapisi?.yazabilir ?? false;
  const kontroller = useEkipmanKontrolleri(equipmentId);
  const secenekler = useGorevSecenekleri();
  const ekle = useKontrolKaydiEkle();
  const sil = useKontrolKaydiSil();
  const raporYukle = useKontrolRaporuYukle();
  const [taslak, setTaslak] = useState<Taslak>(BOS_TASLAK);
  const [hata, setHata] = useState<string | null>(null);

  const tarihIso = gunGirdisiIso(taslak.tarihMetni);
  const dokunuldu =
    taslak.tarihMetni !== '' || taslak.yapanPersonId !== null || taslak.yapanKurulus.trim() !== '';
  // P12 (madde 224) — kaydedilmemiş kontrol kaydı çıkışta uyarır (yönlendirici tek noktada).
  useKaydedilmemisIcerik(dokunuldu ? 1 : 0, 'kontrol kaydı');
  const yapanTekil = (taslak.yapanPersonId !== null) !== (taslak.yapanKurulus.trim() !== '');
  const kaydedilebilir = tarihIso !== null && yapanTekil;

  return (
    <Bolum
      baslik="Periyodik kontroller"
      aciklama={
        'Ekipmanın "son kontrol" tarihi bu listeden okunur: en son UYGUN sonuçlanan kontrol. ' +
        'Bu yüzden künyeden elle yazılamaz — uygun değil sonuçlanan bir kontrol vadeyi ilerletmez.'
      }
      testId="kontrol-listesi"
    >
      {hata && (
        <Alert color={ANLAM.engel} role="alert" data-testid="kontrol-hatasi" mb="xs">
          {hata}
        </Alert>
      )}
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Tarih</Table.Th>
            <Table.Th>Kontrolü yapan</Table.Th>
            <Table.Th>Yetkili sınıfı</Table.Th>
            <Table.Th>Sonuç</Table.Th>
            <Table.Th>Rapor</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {(kontroller.data ?? []).map((satir) => (
            <Table.Tr key={satir.id} data-testid="kontrol-satiri">
              <Table.Td>{tarihGoster(satir.kontrolTarihi)}</Table.Td>
              <Table.Td>
                {satir.devirMi ? (
                  <Rozet anlam="bilgi" testId="kontrol-devir-rozeti">
                    Sistem öncesi kayıt
                  </Rozet>
                ) : (
                  (satir.yapanAd ?? satir.yapanKurulus ?? '—')
                )}
              </Table.Td>
              <Table.Td>
                {satir.kontrolYetkilisi
                  ? EKIPMAN_KONTROL_YETKILISI_ETIKETI[satir.kontrolYetkilisi]
                  : '—'}
              </Table.Td>
              <Table.Td>
                <Rozet
                  anlam={satir.sonuc === 'uygun' ? 'olumlu' : 'engel'}
                  testId="kontrol-sonuc-rozeti"
                >
                  {EKIPMAN_KONTROL_SONUCU_ETIKETI[satir.sonuc]}
                </Rozet>
              </Table.Td>
              <Table.Td>
                {satir.raporDosyaAdi ? (
                  <Anchor
                    href={`/api/equipment-inspections/${satir.id}/rapor`}
                    size="sm"
                    data-testid="kontrol-rapor-baglantisi"
                  >
                    {satir.raporDosyaAdi}
                  </Anchor>
                ) : yazabilir ? (
                  <FileButton
                    onChange={(dosya) => {
                      if (!dosya) return;
                      setHata(null);
                      raporYukle.mutate(
                        { inspectionId: satir.id, dosya },
                        { onError: (e) => setHata(e.message) },
                      );
                    }}
                  >
                    {(props) => (
                      <Button
                        {...props}
                        variant="subtle"
                        size="compact-sm"
                        data-testid="kontrol-rapor-yukle"
                      >
                        Rapor yükle
                      </Button>
                    )}
                  </FileButton>
                ) : (
                  '—'
                )}
              </Table.Td>
              <Table.Td>
                <SatirEylemleri
                  testId="kontrol-eylemleri"
                  digerleri={[
                    {
                      etiket: 'Sil',
                      gizli: !yazabilir,
                      onClick: () => {
                        setHata(null);
                        sil.mutate(satir.id, { onError: (e) => setHata(e.message) });
                      },
                      testId: 'kontrol-sil',
                    },
                  ]}
                />
              </Table.Td>
            </Table.Tr>
          ))}
          {yazabilir && (
            <SolukSatir
              dokunuldu={dokunuldu}
              kaydedilebilir={kaydedilebilir}
              kaydediliyor={ekle.isPending}
              kolonSayisi={6}
              testId="kontrol-soluk-satiri"
              onKaydet={() => {
                if (!tarihIso) return;
                setHata(null);
                ekle.mutate(
                  {
                    equipmentId,
                    input: {
                      kontrolTarihi: tarihIso,
                      sonuc: taslak.sonuc,
                      yapanPersonId: taslak.yapanPersonId,
                      yapanKurulus:
                        taslak.yapanKurulus.trim() === '' ? null : taslak.yapanKurulus.trim(),
                    },
                  },
                  {
                    onSuccess: () => setTaslak(BOS_TASLAK),
                    onError: (e) => setHata(e.message),
                  },
                );
              }}
            >
              <Table.Td>
                {/* ⚠️ 137 DESENİ: takvim seçici + `gunGirdisiIso` doğrulaması —
                  elle yazılan geçersiz tarihte alan SESSİZCE düşmez, düğme
                  kapalı kalır ve sebebi altta yazılıdır. */}
                <TextInput
                  size="xs"
                  type="date"
                  value={taslak.tarihMetni}
                  onChange={(olay) =>
                    setTaslak((o) => ({ ...o, tarihMetni: olay.currentTarget.value }))
                  }
                  data-testid="kontrol-tarihi"
                />
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  placeholder="Kişi"
                  searchable
                  clearable
                  data={(secenekler.data?.personeller ?? []).map((p) => ({
                    value: p.id,
                    label: p.adSoyad,
                  }))}
                  value={taslak.yapanPersonId}
                  onChange={(deger) => setTaslak((o) => ({ ...o, yapanPersonId: deger }))}
                  data-testid="kontrol-yapan-kisi"
                />
              </Table.Td>
              <Table.Td>
                <TextInput
                  size="xs"
                  placeholder="…ya da dış kuruluş"
                  value={taslak.yapanKurulus}
                  onChange={(olay) =>
                    setTaslak((o) => ({ ...o, yapanKurulus: olay.currentTarget.value }))
                  }
                  data-testid="kontrol-yapan-kurulus"
                />
              </Table.Td>
              <Table.Td>
                <Select
                  size="xs"
                  allowDeselect={false}
                  data={[
                    { value: 'uygun', label: EKIPMAN_KONTROL_SONUCU_ETIKETI.uygun },
                    { value: 'uygun_degil', label: EKIPMAN_KONTROL_SONUCU_ETIKETI.uygun_degil },
                  ]}
                  value={taslak.sonuc}
                  onChange={(deger) =>
                    setTaslak((o) => ({
                      ...o,
                      sonuc: deger === 'uygun_degil' ? 'uygun_degil' : 'uygun',
                    }))
                  }
                  data-testid="kontrol-sonucu"
                />
              </Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  Kaydettikten sonra
                </Text>
              </Table.Td>
            </SolukSatir>
          )}
        </Table.Tbody>
      </Table>
      {ekipmanKapisi && (
        <KapiSebebi
          durum={ekipmanKapisi}
          nesne="Kontrol kaydını"
          fiil="girer"
          testId="kontrol-yazamama-sebebi"
        />
      )}
      {dokunuldu && !yapanTekil && (
        <Text size="xs" c={ANLAM.dikkat} data-testid="kontrol-yapan-uyarisi">
          Kontrolü yapan olarak ya bir kişi ya da bir dış kuruluş girin — ikisi birden olmaz.
        </Text>
      )}
    </Bolum>
  );
}

interface Taslak {
  tarihMetni: string;
  yapanPersonId: string | null;
  yapanKurulus: string;
  sonuc: 'uygun' | 'uygun_degil';
}

const BOS_TASLAK: Taslak = {
  tarihMetni: '',
  yapanPersonId: null,
  yapanKurulus: '',
  sonuc: 'uygun',
};
